import services from "../../../Data/demo.js";
import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { FaArrowRight, FaCheck, FaStar, FaRocket, FaLightbulb, FaUsers, FaChevronDown } from "react-icons/fa";
import { FiHome , FiChevronRight } from "react-icons/fi";


const ServiceDetail = () => {
  const { slug } = useParams();
  const service = Object.values(services).find((s) => s.slug === slug);
  const [activeFaq, setActiveFaq] = useState(null);
  // const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };
  const handleScroll = () => {
    window.scrollTo({top:0 , behavior:"smooth"});
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
        mass: 0.5
      }
    }
  };

  const floatVariants = {
    float: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  if (!service) return <h2 className="text-center mt-10 text-2xl font-semibold">Service not found</h2>;

    const Breadcrumb = () => {
      return (
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="hidden items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-center">
              <Link to="/" onClick={handleScroll} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center">
                <FiHome className="w-4 h-4 mr-1" />
                Home
              </Link>
            </li>
            <li className="flex items-center">
              <FiChevronRight className="w-4 h-4 mx-1" />
              <span className="text-blue-600 dark:text-blue-400 font-medium" aria-current="page">Services</span>
            </li>
            <li className="flex items-center">
              <FiChevronRight className="w-4 h-4 mx-1" />
              <span className="text-gray-800 dark:text-gray-200 font-medium" aria-current="page">{service.title}</span>
            </li>
          </ol>
          
          {/* Structured data for Google (invisible to users but helps SEO) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": window.location.origin
              }, {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": window.location.href
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": service.title,
                "item": window.location.href
              }
            ]
            })}
          </script>
        </nav>
      );
    };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{service.seo?.metaTitle || `${service.title} | Your Company`}</title>
        <meta name="description" content={service.seo?.metaDescription || service.description} />
        {service.seo?.keywords && (
          <meta name="keywords" content={Array.isArray(service.seo.keywords) ? service.seo.keywords.join(', ') : service.seo.keywords} />
        )}
        <link rel="canonical" href={`https://merndigital.com/services/${service.slug}`} />
      </Helmet>

      {/* Animated background elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-slate-900">
        {/* Breadcrumb Navigation */}
        <Breadcrumb />

        <div className="max-w-7xl mx-auto px-6 pb-20">
          {/* Hero Section */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="py-16 mb-16 text-center relative"
          >
            {/* Decorative elements */}
            <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>

            <motion.div
              variants={floatVariants}
              animate="float"
              className="inline-flex items-center justify-center p-5 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl mb-8 shadow-lg"
            >
              <span className="text-4xl">{service.icon}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
            >
              {service.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8"
            >
              {service.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="max-w-2xl mx-auto bg-gradient-to-r from-blue-50/80 to-purple-50/80 dark:from-blue-900/20 dark:to-purple-900/20 p-8 rounded-3xl border border-white/30 dark:border-gray-700/30 backdrop-blur-sm shadow-lg"
            >
              <p className="text-lg text-gray-700 dark:text-gray-300">
                {service.briefDescription}
              </p>
            </motion.div>
          </motion.section>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { icon: <FaRocket className="w-8 h-8" />, value: '250+', label: 'Projects Completed', color: 'blue' },
              { icon: <FaStar className="w-8 h-8" />, value: '98%', label: 'Client Satisfaction', color: 'purple' },
              { icon: <FaLightbulb className="w-8 h-8" />, value: '10+', label: 'Years Experience', color: 'green' },
              { icon: <FaUsers className="w-8 h-8" />, value: '24/7', label: 'Support Available', color: 'orange' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all flex flex-col items-center text-center`}
              >
                <div className={`p-3 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30 text-${stat.color}-600 dark:text-${stat.color}-400 mb-4`}>
                  {stat.icon}
                </div>
                <div className={`text-3xl font-bold text-${stat.color}-600 dark:text-${stat.color}-400 mb-2`}>{stat.value}</div>
                <div className="text-gray-600 dark:text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Overview Section */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="mb-20 relative"
          >
            <div className="absolute -left-20 top-1/2 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>

            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center"
            >
              About Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">{service.title}</span> Service
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={itemVariants}>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                  We specialize in delivering exceptional {service.title.toLowerCase()} solutions that drive real business results. Our team of experts combines technical expertise with creative thinking to build solutions that stand out.
                </p>
                <div className="space-y-4">
                  {service.whyUs.slice(0, 4).map((reason, i) => (
                    <motion.div
                      key={i}
                      variants={itemVariants}
                      custom={i}
                      className="flex items-start p-4 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-100/30 dark:border-gray-700/30 shadow-sm"
                    >
                      <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mr-4 mt-1 flex-shrink-0">
                        <FaCheck className="w-3 h-3" />
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{reason}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>


            </div>
          </motion.section>

          {/* Process Steps */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="mb-20"
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center relative pb-3"
            >
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Proven Process</span>
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></span>
            </motion.h2>

            <div className="relative">
              {/* Timeline connector - hidden on mobile */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400/50 to-purple-400/50 transform -translate-x-1/2"></div>

              <ol className="space-y-8 md:space-y-16">
                {service.processSteps.map((step, i) => (
                  <motion.li
                    key={i}
                    variants={itemVariants}
                    className="relative"
                  >
                    {/* Mobile Layout */}
                    <div className="md:hidden">
                      <div className="relative flex items-start gap-4 mb-8">

                        {/* Step Number & Progress Line Container */}
                        <div className="flex flex-col items-center relative">
                          {/* Vertical Connection Line - Only show between steps */}
                          {i < step.length - 1 && (
                            <div className="absolute left-1/2 top-12 w-0.5 h-10 bg-gradient-to-b from-blue-400 to-purple-400 z-0 
                      -translate-x-1/2"></div>
                          )}

                          {/* Step Number Circle */}
                          <div className="relative z-10">
                            <div className="w-12 h-12 flex items-center justify-center rounded-xl 
                      bg-gradient-to-br from-blue-500 to-purple-600
                      text-white font-bold text-lg 
                      shadow-lg shadow-blue-500/25
                      border-2 border-white dark:border-gray-800">
                              {i + 1}
                            </div>

                            {/* Arrow pointing to content */}
                            <div className="absolute -right-3.5 top-1/2 transform -translate-y-1/2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* Step Content Card */}
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: i * 0.1 }}
                          whileHover={{ scale: 1.02 }}
                          className="flex-1 min-w-0 relative"
                        >
                          <div className="p-4 rounded-xl bg-white dark:bg-gray-800
                    border border-gray-200 dark:border-gray-700
                    shadow-lg hover:shadow-xl
                    transition-all duration-300">

                            {/* Progress Status Indicator */}


                            <p className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed 
                    text-sm">
                              {step.split(':').pop()?.trim() || step}
                            </p>

                            {/* Step Navigation */}

                          </div>
                        </motion.div>
                      </div>
                    </div>
                    {/* Desktop/Tablet Layout */}
                    <div className="hidden md:flex w-full">
                      {/* Left Side (even steps) */}
                      <div className={`w-1/2 ${i % 2 === 0 ? 'pr-10' : 'invisible'}`}>
                        {i % 2 === 0 && (
                          <div className="flex justify-end h-full">
                            <div className="max-w-md w-full text-right">
                              <motion.div
                                whileHover={{ scale: 1.02 }}
                                className="p-6 rounded-2xl transition-all duration-300 bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700"
                              >
                                <p className="text-gray-700 dark:text-gray-300 text-right">
                                  {step.split(':')[1] || step}
                                </p>
                              </motion.div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Timeline Dot Center */}
                      <div className="w-16 flex justify-center relative">
                        <div className="absolute top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 shadow-lg">
                          {i + 1}
                        </div>
                      </div>

                      {/* Right Side (odd steps) */}
                      <div className={`w-1/2 ${i % 2 !== 0 ? 'pl-10' : 'invisible'}`}>
                        {i % 2 !== 0 && (
                          <div className="max-w-md w-full">
                            
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              className="p-6 rounded-2xl transition-all duration-300 bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700"
                            >
                              <p className="text-gray-700 dark:text-gray-300">
                                {step.split(':')[1] || step}
                              </p>
                            </motion.div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>
          </motion.section>

          {/* Tech Stack */}
          {service.techStack && (
            <motion.section
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="mb-20"
            >
              <motion.h2
                variants={itemVariants}
                className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center"
              >
                Technology <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Stack</span>
              </motion.h2>

              {/* <div className="grid lg:grid-cols-2 gap-8">
                {Object.entries(service.techStack).map(([category, technologies]) => (
                  <motion.div 
                    key={category} 
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    className="bg-white dark:bg-gray-800 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all"
                  >
                    <h3 className=" text-2xl font-bold text-gray-900 dark:text-white mb-6 capitalize border-b border-gray-200 dark:border-gray-700 pb-4">
                      {category.replace(/([A-Z])/g, ' $1')}
                    </h3>

                   <div className="flex flex-wrap gap-3">
  {Array.isArray(technologies) ? (
    technologies.map((tech, i) => (
      <motion.span
        key={i}
        whileHover={{ scale: 1.05 }}
        className="px-4 py-3 rounded-xl bg-gradient-to-r from-blue-50 to-blue-100 
                   dark:from-blue-900/20 dark:to-blue-800/30 
                   text-blue-700 dark:text-blue-300 
                   border border-blue-200 dark:border-blue-700/30 
                   shadow-sm text-sm font-medium"
      >
        {tech}
      </motion.span>
    ))
  ) : (
    <p className="text-gray-600 dark:text-gray-400">
      {String(technologies)}
    </p>
  )}
</div>

                  </motion.div>
                ))}
              </div> */}
              <div className="grid lg:grid-cols-2 gap-8">
                {typeof service.techStack === 'string' ? (
                  <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-300 text-lg">{service.techStack}</p>
                  </div>
                ) : Array.isArray(service.techStack) ? (
                  <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex flex-wrap gap-3">
                      {service.techStack.map((tech, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                          viewport={{ once: true }}
                          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-300"
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ) : (
                  Object.entries(service.techStack).map(([category, technologies]) => (
                    <motion.div
                      key={category}
                      variants={itemVariants}
                      className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700"
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 capitalize border-b border-gray-200 dark:border-gray-700 pb-3">
                        {category.replace(/([A-Z])/g, ' $1')}
                      </h3>

                      <div className="flex flex-wrap gap-3">
                        {typeof technologies === 'string' ? (
                          technologies.split(', ').map((tech, i) => (
                            <span
                              key={i}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                            >
                              {tech}
                            </span>
                          ))
                        ) : Array.isArray(technologies) ? (
                          technologies.map((tech, i) => (
                            <span
                              key={i}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                            >
                              {tech}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-600 dark:text-gray-400">{technologies}</p>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.section>
          )}

          {/* Sub Services */}
          {service.subServices && service.subServices.length > 0 && (
            <motion.section
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="mb-20"
            >
              <motion.h2
                variants={itemVariants}
                className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center"
              >
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Sub-Services</span>
              </motion.h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                
                {service.subServices.map((sub) => (
                  <Link to={`/services/${service.slug}/${sub.slug}`}
                      onClick={handleScroll}>
                  <motion.div
                    key={sub.id}
                    variants={itemVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className="bg-white dark:bg-gray-800 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all shadow-lg hover:shadow-xl group"
                  >
                    
                    <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <span className="text-2xl">{service.icon}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{sub.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">{sub.briefDescription}</p>
                    <span
                      t
                      className="inline-flex items-center text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors group/link"
                    >
                      Learn More
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ml-2"
                      >
                        <FaArrowRight className="w-4 h-4" />
                      </motion.span>
                    </span>
                    
                  </motion.div>
                  </Link>
                ))}
                
              </div>
            </motion.section>
          )}
          {/* FAQ Section */}
          {service.faq && service.faq.length > 0 && (
            <motion.section
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="mb-20"
            >
              <motion.h2
                variants={itemVariants}
                className="text-4xl font-bold text-gray-900 dark:text-white mb-12 text-center"
              >
                Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Questions</span>
              </motion.h2>

              <div className="max-w-4xl mx-auto">
                {service.faq.map((faq, i) => (
                  <motion.div
                    key={i}
                    variants={itemVariants}
                    className="mb-6 last:mb-0"
                  >
                    <motion.button
                      onClick={() => toggleFaq(i)}
                      className="flex items-center justify-between w-full p-6 text-left bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all"
                      whileHover={{ y: -2 }}
                      aria-expanded={activeFaq === i}
                    >
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white pr-4">
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: activeFaq === i ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg"
                      >
                        <FaChevronDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </motion.div>
                    </motion.button>

                    <AnimatePresence>
                      {activeFaq === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-b-2xl border border-t-0 border-gray-200 dark:border-gray-700">
                            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
          {/* CTA Section */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-14 rounded-4xl text-white">
              {/* Animated background elements */}
              <div className="absolute top-0 left-0 w-full h-full opacity-10">
                <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
              </div>

              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold mb-8">
                  Ready to Transform Your Business?
                </h2>
                <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                  Let's discuss how our {service.title} services can drive growth and innovation for your organization.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link onClick={handleScroll}
                      to={service.cta?.link || "/contact"}
                      className="inline-flex items-center justify-center px-10 py-5 bg-white text-blue-600 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all shadow-2xl hover:shadow-3xl"
                    >
                      {service.cta?.text || "Start Your Project"}
                      <FaArrowRight className="w-5 h-5 ml-3" />
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link onClick={handleScroll}
                      to="/services"
                      className="inline-flex items-center justify-center px-10 py-5 bg-transparent border-2 border-white text-white rounded-2xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all"
                    >
                      Explore All Services
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;